import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useEvents } from '@entities/event';
import type { ApiEvent } from '@entities/event';

/** Sortierrichtung für die Event-Liste. */
type SortOrder = 'alpha' | 'date';

/**
 * Filtert und sortiert Events nach Suchbegriff und gewählter Sortierung.
 *
 * @param events - Rohe Event-Liste von der API
 * @param query - Suchbegriff (wird auf Titel und Ort angewendet)
 * @param sortOrder - Sortierrichtung: alphabetisch oder nach Datum
 * @returns Gefilterte und sortierte Event-Liste
 */
function filterAndSortEvents(events: ApiEvent[], query: string, sortOrder: SortOrder): ApiEvent[] {
  const needle = query.trim().toLowerCase();

  const filtered = needle
    ? events.filter(
        (e) =>
          e.title.toLowerCase().includes(needle) ||
          (e.location ?? '').toLowerCase().includes(needle),
      )
    : events;

  return [...filtered].sort((a, b) => {
    if (sortOrder === 'alpha') {
      return a.title.localeCompare(b.title, 'de');
    }
    return new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime();
  });
}

/**
 * Seite zur Auswahl eines Events vor dem Scan-Vorgang.
 * Lädt veröffentlichte Events und speichert die Auswahl in sessionStorage.
 * Bietet Suche (nach Titel/Ort) und Sortierung (alphabetisch / nach Datum).
 *
 * @returns Event-Auswahl-Seite mit Suche und Sortierung
 */
export function SelectEventPage() {
  const navigate = useNavigate();
  const { data: events, isLoading, isError } = useEvents();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState<SortOrder>('date');

  const handleSelectEvent = (event: ApiEvent) => {
    sessionStorage.setItem('selectedEventId', event.id);
    sessionStorage.setItem('selectedEventTitle', event.title);
    void navigate({ to: '/' });
  };

  const filteredEvents = events ? filterAndSortEvents(events, searchQuery, sortOrder) : [];

  return (
    <div
      style={{
        minHeight: '100dvh',
        backgroundColor: '#0f0f23',
        color: '#ffffff',
        padding: '24px 16px',
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>Event auswählen</h1>
      <p style={{ color: '#aaa', marginBottom: '20px', fontSize: '14px' }}>
        Wähle das Event aus, für das du einchecken möchtest.
      </p>

      {/* Such- und Sortierleiste */}
      {events && events.length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '20px',
            alignItems: 'center',
          }}
        >
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Event suchen..."
            aria-label="Events durchsuchen"
            style={{
              flex: 1,
              minHeight: '44px',
              padding: '10px 14px',
              fontSize: '15px',
              backgroundColor: '#1a1a2e',
              color: '#ffffff',
              border: '1px solid #333',
              borderRadius: '8px',
              outline: 'none',
            }}
          />
          <button
            onClick={() => setSortOrder((prev) => (prev === 'alpha' ? 'date' : 'alpha'))}
            title={sortOrder === 'alpha' ? 'Nach Datum sortieren' : 'Alphabetisch sortieren'}
            style={{
              minHeight: '44px',
              minWidth: '44px',
              padding: '10px 12px',
              backgroundColor: '#1a1a2e',
              color: '#aaa',
              border: '1px solid #333',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '13px',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {sortOrder === 'alpha' ? 'A–Z' : 'Datum'}
          </button>
        </div>
      )}

      {isLoading && (
        <div style={{ textAlign: 'center', color: '#aaa', padding: '40px 0' }}>Lade Events...</div>
      )}

      {isError && (
        <div
          style={{
            backgroundColor: '#2d1515',
            border: '1px solid #f87171',
            borderRadius: '8px',
            padding: '16px',
            color: '#f87171',
          }}
        >
          Events konnten nicht geladen werden. Bitte Verbindung prüfen.
        </div>
      )}

      {events && events.length === 0 && (
        <div style={{ textAlign: 'center', color: '#aaa', padding: '40px 0' }}>
          Keine aktiven Events gefunden.
        </div>
      )}

      {events && events.length > 0 && filteredEvents.length === 0 && (
        <div style={{ textAlign: 'center', color: '#aaa', padding: '40px 0' }}>
          Kein Event gefunden für „{searchQuery}".
        </div>
      )}

      {filteredEvents.length > 0 && (
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
          }}
        >
          {filteredEvents.map((event) => (
            <li key={event.id}>
              <button
                onClick={() => handleSelectEvent(event)}
                style={{
                  width: '100%',
                  minHeight: '72px',
                  padding: '16px',
                  backgroundColor: '#1a1a2e',
                  border: '1px solid #333',
                  borderRadius: '12px',
                  color: '#ffffff',
                  textAlign: 'left',
                  cursor: 'pointer',
                  touchAction: 'manipulation',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '4px',
                }}
              >
                <span style={{ fontWeight: 'bold', fontSize: '16px' }}>{event.title}</span>
                <span style={{ color: '#aaa', fontSize: '13px' }}>
                  {new Date(event.startsAt).toLocaleDateString('de-DE', {
                    weekday: 'short',
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                  {event.location ? ` — ${event.location}` : ''}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
